/**
 * /tools/:short_id — user-facing Tool record page.
 *
 * Per D-114 record-page discipline: every assignable-type row in the DB gets
 * a stable URL + lightning-highlight + tabs + rail. This is the Tool record's
 * surface for end users.
 *
 * Read-only for users. Editable equivalent lives at
 * /admin/tools/:short_id (Phase 2 Block 2A flattening).
 *
 * Data: fetched via fetchMyToolDetail(short_id), which returns BOTH the
 * matching tool_instance AND its parent connector context (breadcrumbs +
 * "back to connector" + parent connector activity feed).
 *
 * Phase 2 Block 2A (2026-06-30): URL flattened from
 *   /connectors/:slug/tools/:tool_id  →  /tools/:short_id
 * The single short_id (tli-prefix) deterministically identifies the tool;
 * the parent connector is resolved server-side via the JOIN in the Pages
 * Function. Closes two ambiguity classes at once: shared-slug collisions
 * AND nested URLs that hide the multi-instance shape.
 *
 * Tabs:
 *   - Overview — when_to_use, an example prompt
 *   - Activity — recent dispatches that called this tool (filter from the
 *     connector's recent_activity)
 *   - Notes — admin-set usage_guidance, read-only
 *
 * Rail:
 *   - Connection — LookupField back to the parent Connector
 *   - Access — access_class pill + permission_mode pill
 *   - Cost — cost_units per call
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useSession } from '../hooks/useSession.tsx';
import {
  fetchMyToolDetail,
  ApiError,
  type MyToolDetail,
} from '../lib/api.ts';
import { Breadcrumb } from '../components/Breadcrumb.tsx';
import { ObjectIcon } from '../components/ObjectIcon.tsx';
import { LookupField } from '../components/LookupField.tsx';
import { ConnectorLogo } from '../components/ConnectorLogo.tsx';
// Push 5-F (2026-07-11): record-detail full-page gate → SkeletonDetail.
import { SkeletonDetail } from '../components/Skeleton.tsx';
import { Pill } from '../components/StatusPill.tsx';
import { DispatchStatusPill } from '../components/StatusPill.tsx';
import { CopyButton } from '../components/CopyButton.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { LightningTabs, type LightningTab } from '../components/LightningTabs.tsx';
import { RecordActions } from '../components/RecordActions.tsx';
import { RecordDetailLayout } from '../components/RecordDetailLayout.tsx';
import { formatTimestamp } from '../lib/format-timestamp.ts';

const ACCESS_CLASS_LABEL: Record<'read' | 'write' | 'destructive', string> = {
  read: 'Read',
  write: 'Create or change',
  destructive: 'Remove',
};

const ACCESS_CLASS_KIND: Record<'read' | 'write' | 'destructive', 'success' | 'info' | 'danger'> = {
  read: 'success',
  write: 'info',
  destructive: 'danger',
};

export function ToolDetail() {
  // Phase 2 Block 2A (2026-06-30) — flat URL /tools/:short_id. :short_id is
  // tool_instance.short_id (tli-prefix). Server returns { tool, connector,
  // recent_activity } in one round-trip via JOIN; no client-side filter needed.
  const { short_id = '' } = useParams<{ short_id: string }>();
  const { user } = useSession();
  const [detail, setDetail] = useState<MyToolDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('overview');

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    fetchMyToolDetail(short_id)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Could not load tool.');
      });
    return () => {
      cancelled = true;
    };
  }, [short_id]);

  if (user === null) return null;

  if (error !== null) {
    return (
      <div>
        <Breadcrumb
          items={[
            { to: '/home', label: 'Home' },
            { to: '/connectors', label: 'My connectors' },
            { label: 'Tool' },
          ]}
        />
        <div className="lightning-highlight">
          <div className="lightning-highlight-left">
            <ObjectIcon kind="connectors" size="lg" />
            <div className="lightning-highlight-text">
              <h1 className="lightning-title">Not available</h1>
              <p
                className="muted"
                style={{ margin: '6px 0 0', fontSize: '13px', maxWidth: '72ch', lineHeight: 1.45 }}
              >
                {error}
              </p>
            </div>
          </div>
          <RecordActions actions={[]} />
        </div>
      </div>
    );
  }

  if (detail === null) return <SkeletonDetail />;

  // Phase 2 Block 2A — server returns the tool directly (no client-side find).
  // The 404 case is the error branch above; tool is non-null by this point.
  const { tool, connector } = detail;
  const accessClass = (tool.access_class ?? 'read') as 'read' | 'write' | 'destructive';
  // Server already filtered recent_activity to primitive_instance_id = this
  // tool's instance.id, so no client-side narrowing needed.
  const dispatchesForThisTool = detail.recent_activity;
  const examplePrompt = generateExamplePrompt(tool, connector.display_name);

  return (
    <div>
      <Breadcrumb
        items={[
          { to: '/home', label: 'Home' },
          { to: '/connectors', label: 'My connectors' },
          {
            to: `/connectors/${connector.short_id}`,
            label: connector.display_name,
            icon: (
              <ConnectorLogo
                slug={connector.template_name}
                name={connector.display_name}
                logoUrl={connector.logo_url}
                size="sm"
              />
            ),
          },
          { label: tool.display_name },
        ]}
      />

      {/* Phase 2 Block 2B EXISTS-MIGRATE (2026-06-30) — RecordDetailLayout
         primitive wraps hero + main + rail. Same visual output as the prior
         hand-rolled .lightning-highlight + .record-shell + .record-rail
         structure; structural enforcement via the canonical Cufflinks 2.0
         layout from Round 6 Item 16. ConnectorDetail.tsx was the reference
         implementation; ToolDetail follows the same composition. */}
      <RecordDetailLayout
        hero={
          <div className="lightning-highlight">
            <div className="lightning-highlight-left">
              <ObjectIcon kind="connectors" size="lg" />
              <div className="lightning-highlight-text">
                {/* Push 4-1ao (2026-07-11): .lightning-breadcrumb arrow-link
                    deleted per universal record-detail back-nav scrub. */}
                <h1 className="lightning-title">{tool.display_name}</h1>
                <div className="lightning-meta">
                  <Pill kind={ACCESS_CLASS_KIND[accessClass]}>
                    {ACCESS_CLASS_LABEL[accessClass]}
                  </Pill>
                  {tool.cost_units > 0 && (
                    <span className="muted">{tool.cost_units} unit{tool.cost_units === 1 ? '' : 's'}/call</span>
                  )}
                </div>
              </div>
            </div>
            <RecordActions
              actions={[]}
            />
          </div>
        }
        main={
          <div className="record-surface record-surface--tabs-only">
          {(() => {
            const tabs: LightningTab[] = [
              {
                id: 'overview',
                label: 'Overview',
                render: () => (
                  <div className="lightning-section">
                    {tool.when_to_use !== null && tool.when_to_use.trim() !== '' && (
                      <>
                        <h3
                          style={{
                            margin: '0 0 6px',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: 'var(--olytic-obsidian)',
                          }}
                        >
                          When to use this
                        </h3>
                        <p
                          style={{
                            margin: '0 0 20px',
                            fontSize: '14px',
                            color: 'var(--fg)',
                            lineHeight: 1.5,
                          }}
                        >
                          {tool.when_to_use}
                        </p>
                      </>
                    )}

                    <div className="card card-padded">
                      <div
                        style={{
                          fontSize: '10px',
                          fontWeight: 600,
                          color: 'var(--fg-muted)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          marginBottom: '8px',
                        }}
                      >
                        Try asking
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '10px 14px',
                          background: 'var(--olytic-surface)',
                          border: '1px solid var(--border)',
                          borderRadius: '8px',
                        }}
                      >
                        <div
                          style={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: '13px',
                            fontStyle: 'italic',
                            color: 'var(--olytic-obsidian)',
                          }}
                        >
                          "{examplePrompt}"
                        </div>
                        <CopyButton
                          value={examplePrompt}
                          successMessage="Prompt copied. Paste into your AI assistant."
                        >
                          Copy
                        </CopyButton>
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                id: 'activity',
                label: 'Activity',
                count: dispatchesForThisTool.length,
                render: () => (
                  <div className="lightning-section">
                    {dispatchesForThisTool.length === 0 ? (
                      <EmptyState
                        kind="activity"
                        title="No activity yet"
                        body={`Once your assistant uses ${tool.display_name}, the activity shows up here.`}
                      />
                    ) : (
                      <div className="activity-list">
                        {dispatchesForThisTool.map((a) => (
                          <div key={a.id} className="activity-row">
                            <div>
                              <div className="activity-primary">Activity</div>
                              <div className="activity-sub">
                                {formatTimestamp(a.created_at, user?.timezone, user?.account_timezone)}
                              </div>
                            </div>
                            <DispatchStatusPill status={a.status} />
                            <div className="activity-meta">HTTP {a.vendor_http_code ?? '-'}</div>
                            <div className="activity-meta">
                              {a.duration_ms !== null ? `${a.duration_ms}ms` : '-'}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ),
              },
            ];
            return <LightningTabs tabs={tabs} value={activeTab} onChange={setActiveTab} />;
          })()}
          </div>
        }
        rail={
          <>
            <div className="rail-card">
              <p className="rail-card-label">Connection</p>
              <LookupField
                label="Connector"
                to={`/connectors/${connector.short_id}`}
                name={connector.display_name}
                icon={
                  <ConnectorLogo
                    slug={connector.template_name}
                    name={connector.display_name}
                    logoUrl={connector.logo_url}
                    size="sm"
                  />
                }
              />
            </div>

            <div className="rail-card">
              <p className="rail-card-label">Access</p>
              <div className="rail-row">
                <span className="rail-row-label">Class</span>
                <span className="rail-row-value">
                  <Pill kind={ACCESS_CLASS_KIND[accessClass]}>
                    {ACCESS_CLASS_LABEL[accessClass]}
                  </Pill>
                </span>
              </div>
              <div className="rail-row">
                <span className="rail-row-label">Cost per call</span>
                <span className="rail-row-value">
                  {tool.cost_units > 0 ? `${tool.cost_units} unit${tool.cost_units === 1 ? '' : 's'}` : 'Free'}
                </span>
              </div>
            </div>

            {dispatchesForThisTool.length > 0 && (
              <div className="rail-card">
                <p className="rail-card-label">Recent activity</p>
                <div className="rail-row">
                  <span className="rail-row-label">Calls (last batch)</span>
                  <span className="rail-row-value">{dispatchesForThisTool.length}</span>
                </div>
              </div>
            )}
          </>
        }
      />
    </div>
  );
}

function generateExamplePrompt(
  tool: MyToolDetail['tool'],
  connectorName: string,
): string {
  if (tool.when_to_use !== null && tool.when_to_use.length > 10) {
    return tool.when_to_use;
  }
  const verb = tool.operation_verb ?? 'use';
  return `Use ${connectorName} to ${verb} ${tool.display_name.toLowerCase()}.`;
}
